import { useState, useEffect } from 'react';
import { txApi } from '../txApi.js';
import { C, btn, Err, Spinner, inp, useConfirm } from './_shared.jsx';
import SpreadsheetGrid, { uid } from './_SpreadsheetGrid.jsx';

export default function VersionPreviewPage({ campaignId, versionId, onBack, onRestored }) {
  const [group,      setGroup]      = useState(null);
  const [allVersions, setAllVersions] = useState([]);
  const [version,    setVersion]    = useState(null);
  const [taxonomy,   setTaxonomy]   = useState(null);
  const [dictValues, setDictValues] = useState({});
  const [loading,    setLoading]    = useState(true);
  const [restoring,  setRestoring]  = useState(false);
  const [err,        setErr]        = useState(null);
  const [confirmUI, ask] = useConfirm();

  // Selected platform/level (derived from current version, switchable)
  const [selPlatformId, setSelPlatformId] = useState(null);
  const [selLevel,      setSelLevel]      = useState(null);

  // Unique platforms and levels from all versions
  const platforms = (() => {
    const seen = new Map();
    allVersions.forEach(v => { if (v.platform_id && !seen.has(v.platform_id)) seen.set(v.platform_id, v.platform_name || `Platform ${v.platform_id}`); });
    return [...seen.entries()].map(([id, name]) => ({ id, name }));
  })();

  const levelsForPlatform = [...new Set(allVersions.filter(v => String(v.platform_id) === String(selPlatformId)).map(v => v.level))];

  // Initial load: group + specific version + all versions
  useEffect(() => {
    Promise.all([
      txApi.getCampaignGroup(campaignId),
      txApi.getGroupVersion(campaignId, versionId),
      txApi.getGroupVersions(campaignId),
    ]).then(([gd, vd, ad]) => {
      const grp = gd.group || {};
      const ver = vd.version || {};
      setGroup(grp);
      setAllVersions(ad.versions || []);
      setSelPlatformId(ver.platform_id || grp.platform_id);
      setSelLevel(ver.level);
      setVersion(ver);
    }).catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, [campaignId, versionId]);

  // Load taxonomy whenever version changes
  useEffect(() => {
    if (!group || !version) return;
    const pid = version.platform_id || group.platform_id;
    const lv  = version.level;
    if (!pid || !lv) return;
    setTaxonomy(null);
    txApi.getTaxonomy(group.client_id, pid, lv).then(async td => {
      const tax = td.taxonomy;
      setTaxonomy(tax);
      const dictIds = [...new Set((tax?.fields || []).filter(f => f.dict_list_id).map(f => f.dict_list_id))];
      if (dictIds.length) {
        const results = await Promise.all(dictIds.map(id => txApi.getDictValues(id).then(r => [id, (r.values || []).filter(v => !v.is_excluded)]).catch(() => [id, []])));
        setDictValues(Object.fromEntries(results));
      }
    }).catch(() => {});
  }, [group, version]);

  // When user switches platform or level, find latest version for that combo
  useEffect(() => {
    if (!selPlatformId || !selLevel || !allVersions.length) return;
    const match = allVersions.find(v => String(v.platform_id) === String(selPlatformId) && v.level === selLevel);
    if (!match) return;
    if (match.id === version?.id) return;
    txApi.getGroupVersion(campaignId, match.id)
      .then(vd => setVersion(vd.version || {}))
      .catch(() => {});
  }, [selPlatformId, selLevel, allVersions]);

  const restore = async () => {
    const okay = await ask({
      title: `Restore v${version.version_num} for the "${version.level}" level?`,
      body: 'The current version is saved as a new snapshot first, so nothing is lost.',
      confirmLabel: 'Restore this version',
    });
    if (!okay) return;
    setRestoring(true);
    try {
      await txApi.restoreGroupVersion(campaignId, version.id);
      onRestored();
    } catch (e) {
      setErr(e.message);
      setRestoring(false);
    }
  };

  const snapshot = version?.snapshot || [];
  const fields   = taxonomy?.fields || [];

  const gridRows = snapshot.map(row => {
    const fv = typeof row.field_values === 'string' ? JSON.parse(row.field_values) : (row.field_values || {});
    const values = {};
    fields.forEach(f => { values[f.id] = fv[String(f.id)] || fv[f.id] || ''; });
    return { id: uid(), values, _taxonomy_string: row.taxonomy_string };
  });

  const lbl = (x = {}) => ({ fontSize: 10, fontWeight: 600, letterSpacing: '0.09em', color: C.muted, textTransform: 'uppercase', ...x });

  return (
    <div>
      {/* Top bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, gap: 12 }}>
        <button onClick={onBack} style={{ ...btn('ghost'), display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          ← Back to Campaigns
        </button>
        {!loading && version && (
          <button onClick={restore} disabled={restoring} style={{ ...btn('primary'), padding: '8px 20px', fontSize: 13 }}>
            {restoring ? 'Restoring…' : 'Restore this Version'}
          </button>
        )}
      </div>

      <Err msg={err} />

      {loading ? <Spinner /> : (
        <>
          {/* Version info card */}
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: '18px 22px', marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={lbl({ marginBottom: 3 })}>Campaign</div>
                <div style={{ fontWeight: 700, fontSize: 16, color: C.text }}>{group?.name}</div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{group?.client_name}</div>
              </div>
              <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                <div>
                  <div style={lbl({ marginBottom: 3 })}>Version</div>
                  <span style={{ fontFamily: C.mono, fontSize: 13, fontWeight: 600, color: C.brass }}>
                    v{version?.version_num}
                  </span>
                </div>
                <div>
                  <div style={lbl({ marginBottom: 3 })}>Saved By</div>
                  <div style={{ fontSize: 13, color: C.text }}>{version?.saved_by_name}</div>
                </div>
                <div>
                  <div style={lbl({ marginBottom: 3 })}>Date & Time</div>
                  <div style={{ fontSize: 13, color: C.text }}>{new Date(version?.saved_at).toLocaleString()}</div>
                </div>
                <div>
                  <div style={lbl({ marginBottom: 3 })}>Rows</div>
                  <div style={{ fontSize: 13, color: C.text }}>{snapshot.length}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Platform + Level selectors */}
          {platforms.length > 0 && (
            <div style={{ display: 'flex', gap: 24, marginBottom: 20, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              {platforms.length > 1 && (
                <div>
                  <div style={lbl({ marginBottom: 6 })}>Channel</div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {platforms.map(p => (
                      <button key={p.id} onClick={() => { setSelPlatformId(p.id); setSelLevel(levelsForPlatform[0]); }}
                        style={{ ...btn(String(p.id) === String(selPlatformId) ? 'primary' : 'ghost'), fontSize: 12, padding: '5px 14px' }}>
                        {p.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {levelsForPlatform.length > 0 && (
                <div>
                  <div style={lbl({ marginBottom: 6 })}>Level</div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {levelsForPlatform.map(lv => (
                      <button key={lv} onClick={() => setSelLevel(lv)}
                        style={{ ...btn(lv === selLevel ? 'primary' : 'ghost'), fontSize: 12, padding: '5px 14px' }}>
                        {lv}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Snapshot grid */}
          {snapshot.length === 0 ? (
            <div style={{ color: C.muted, fontSize: 13, textAlign: 'center', padding: '40px 0', fontStyle: 'italic' }}>
              This version has no rows.
            </div>
          ) : taxonomy ? (
            <SpreadsheetGrid
              taxonomy={taxonomy}
              dictValues={dictValues}
              rows={gridRows}
              setRows={() => {}}
              readOnly={true}
            />
          ) : (
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
              {snapshot.map((row, i) => (
                <div key={i} style={{ fontFamily: C.mono, fontSize: 10, color: C.code, padding: '8px 12px', borderBottom: i < snapshot.length - 1 ? `1px solid ${C.border}28` : 'none', wordBreak: 'break-all' }}>
                  {row.taxonomy_string}
                </div>
              ))}
            </div>
          )}
        </>
      )}
      {confirmUI}
    </div>
  );
}
